import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Base from "../admin_components/Admin_Base";
import { Button, Card, Row, Col, Badge } from "react-bootstrap";
import { Breadcrumbs } from "@mui/material";
import Link from "@mui/material/Link";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import authAxios from "../Auth/useAxios";
import { ADMIN_API_BASE_URL } from "../Auth/Api";
import { ClipLoader } from "react-spinners";
import EditIcon from "@mui/icons-material/Edit";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  FaUser,
  FaPhone,
  FaEnvelope,
  FaIdCard,
  FaCalendarAlt,
  FaBuilding
} from "react-icons/fa";

const CustomerView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCustomerDetails();
  }, [id]);

  const fetchCustomerDetails = async () => {
    try {
      const response = await authAxios.get(
        `${ADMIN_API_BASE_URL}/main/admin/customer/${id}`
      );
      setCustomer(response.data);
      console.log("Customer data:", response.data);
    } catch (error) {
      console.error("Error fetching customer details:", error);
      setError("Failed to fetch customer details");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    navigate(`/admin/customer-edit/${id}`);
  };

  const handleBack = () => {
    navigate("/admin/customer-management");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const InfoItem = ({ icon: Icon, label, value, badge }) => (
    <div className="d-flex justify-content-between align-items-start border-bottom py-3">
      <div className="d-flex align-items-center">
        <Icon className="text-muted me-2" />
        <span className="fw-semibold text-dark">{label}:</span>
      </div>
      <div className="text-end">
        {badge ? (
          <Badge bg={value ? "success" : "secondary"}>
            {value ? "Active" : "Inactive"}
          </Badge>
        ) : (
          <span className={value ? "text-dark" : "text-muted"}>{value || "N/A"}</span>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <Base>
        <div className="d-flex justify-content-center align-items-center" style={{ height: "50vh" }}>
          <ClipLoader color="darkslategrey" size={50} />
        </div>
      </Base>
    );
  }

  if (error) {
    return (
      <Base>
        <div className="alert alert-danger text-center mx-3">{error}</div>
      </Base>
    );
  }

  if (!customer) {
    return (
      <Base>
        <div className="alert alert-warning text-center mx-3">Customer not found</div>
      </Base>
    );
  }

  return (
    <Base>
      <div className="container-fluid pt-3 mt-5">
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
          <Link underline="hover" color="inherit" href="/admin/dashboard" sx={{ color: "darkslategrey", fontWeight: "bold" }}>
            Dashboard
          </Link>
          <Link underline="hover" color="inherit" href="/admin/customer-management" sx={{ color: "darkslategrey", fontWeight: "bold" }}>
            Customer Management
          </Link>
          <Link underline="none" color="inherit" sx={{ color: "darkslategrey", fontWeight: "bold" }}>
            View Customer
          </Link>
        </Breadcrumbs>
      </div>

      <div className="container-fluid mt-3">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 className="fw-bold text-dark mb-1">Customer Details</h2>
            <p className="text-muted mb-0">Complete information about the customer</p>
          </div>
          <div className="d-flex gap-2">
            <Button variant="outline-secondary" onClick={handleBack}>
              <ArrowBackIcon className="me-1" /> Back to List
            </Button>
            <Button variant="primary" onClick={handleEdit}>
              <EditIcon className="me-1" /> Edit Customer
            </Button>
          </div>
        </div>

        <Row>
          {/* Customer Information */}
          <Col md={8}>
            {/* Personal Information */}
            <Card className="mb-4 shadow-sm border-0">
              <Card.Header className="bg-light border-0">
                <Card.Title className="mb-0 d-flex align-items-center">
                  <FaUser className="me-2 text-primary" />
                  Personal Information
                </Card.Title>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <InfoItem icon={FaUser} label="Customer Name" value={customer.customerName} />
                    <InfoItem icon={FaPhone} label="Mobile Number" value={customer.mobileNumber} />
                    <InfoItem icon={FaEnvelope} label="Email" value={customer.email} />
                    <InfoItem icon={FaCalendarAlt} label="Joined At" value={formatDate(customer.joinedAt)} />
                  </Col>
                  <Col md={6}>
                    <InfoItem icon={FaBuilding} label="Address" value={customer.primaryAddress} />
                    <InfoItem icon={FaCalendarAlt} label="Status" value={customer.status} badge />
                    <InfoItem icon={FaCalendarAlt} label="Date of Birth" value={formatDate(customer.dateOfBirth)} />
                    <InfoItem icon={FaCalendarAlt} label="Signed At" value={formatDate(customer.signedAt)} />
                  </Col>
                </Row>
                
                {customer.gender && (
                  <InfoItem icon={FaUser} label="Gender" value={customer.gender} />
                )}
              </Card.Body>
            </Card>

            {/* Employee Information */}
            {customer.employee && (
              <Card className="mb-4 shadow-sm border-0">
                <Card.Header className="bg-light border-0">
                  <Card.Title className="mb-0 d-flex align-items-center">
                    <FaUser className="me-2 text-primary" />
                    Assigned Employee
                  </Card.Title>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <InfoItem icon={FaUser} label="Employee Name" value={customer.employee.fullName} />
                      <InfoItem icon={FaIdCard} label="Employee ID" value={customer.employee.employeeReferenceId} />
                    </Col>
                    <Col md={6}>
                      <InfoItem icon={FaBuilding} label="Designation" value={customer.employee.designation} />
                      <InfoItem icon={FaEnvelope} label="Employee Email" value={customer.employee.email} />
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            )}

            {/* Document Information */}
            <Card className="mb-4 shadow-sm border-0">
              <Card.Header className="bg-light border-0">
                <Card.Title className="mb-0 d-flex align-items-center">
                  <FaIdCard className="me-2 text-primary" />
                  Document Information
                </Card.Title>
              </Card.Header>
              <Card.Body>
                <InfoItem icon={FaIdCard} label="Document Name" value={customer.documentName} />
                {customer.documentFile && (
                  <div className="d-flex justify-content-between align-items-center border-bottom py-3">
                    <div className="d-flex align-items-center">
                      <FaIdCard className="text-muted me-2" />
                      <span className="fw-semibold text-dark">Document File:</span>
                    </div>
                    <a 
                      href={customer.documentFile} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="btn btn-outline-primary btn-sm"
                    >
                      View Document
                    </a>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* Images Section */}
          <Col md={4}>
            <img 
              title="Profile Image" 
              src={customer.profileImagePath} 
              alt=""
              style={{ width: '90%' }}
            />
            
            <img
              title="Signature" 
              src={customer.signatureImage} 
              style={{ width: '90%' }}
              alt=""
            />
            
            <img
              title="Aadhar Card" 
              src={customer.aadharImage} 
              style={{ width: '90%' }}
              alt=""
            />

            <img
              title=" Pan Card" 
              src={customer.panImage} 
              style={{ width: '90%' }}
              alt=""
            />
          </Col>
        </Row>
      </div>
    </Base>
  );
};

export default CustomerView;